import React, { useState, type ReactNode, useEffect } from "react";
import {
  Fab,
  Tooltip,
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Modal,
  Chip,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";

import BugReportIcon from "@mui/icons-material/BugReport";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CustomTextField from "../../components/common/Textfield";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SpeedIcon from "@mui/icons-material/Speed";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import { ImageOutlined, VideocamOutlined } from "@mui/icons-material";
import apiService from "../../service/Api";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/permissions";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBugAdminDashboard,
  resetBugState,
  fetchBugs,
} from "../../store/slice/bug-fix/bugSlice";
import { AppDispatch, RootState } from "../../store/Index";
import { BugReport } from "../../store/slice/bug-fix/types";

/* =====================
   Context helper functions
===================== */

const getModuleNameFromPath = (path: string) => {
  const firstSegment = path.split("/").filter(Boolean)[0] || "";
  if (!firstSegment) return "Unknown";
  return firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1);
};

/** Formats "/bookings/graphics" → "Bookings → Graphics" */
const formatPathDisplay = (path: string) => {
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return "/";
  return segments
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" → ");
};

/** Returns OS name + version e.g. "macOS 14.2" or "Windows 11" */
const getOSInfo = (userAgent: string) => {
  const ua = userAgent;
  if (ua.includes("Mac OS X")) {
    const match = ua.match(/Mac OS X ([\d_]+)/);
    const version = match ? match[1].replace(/_/g, ".") : "";
    return `macOS ${version}`;
  }
  if (ua.includes("Windows NT")) {
    const match = ua.match(/Windows NT ([\d.]+)/);
    const versions: Record<string, string> = {
      "10.0": "10 / 11",
      "6.3": "8.1",
      "6.2": "8",
      "6.1": "7",
    };
    return `Windows ${versions[match?.[1] ?? ""] ?? match?.[1] ?? ""}`;
  }
  if (ua.includes("Android")) {
    const match = ua.match(/Android ([\d.]+)/);
    return `Android ${match?.[1] ?? ""}`;
  }
  if (ua.includes("iPhone OS") || ua.includes("iPad")) {
    const match = ua.match(/OS ([\d_]+)/);
    return `iOS ${match?.[1].replace(/_/g, ".") ?? ""}`;
  }
  if (ua.includes("Linux")) return "Linux";
  return "Unknown OS";
};

const getBrowserName = (userAgent: string) => {
  const ua = userAgent.toLowerCase();
  if (ua.includes("edg/")) return "Microsoft Edge";
  if (ua.includes("opr/") || ua.includes("opera")) return "Opera";
  if (ua.includes("chrome/") && !ua.includes("edg/")) return "Chrome";
  if (ua.includes("safari/") && !ua.includes("chrome/")) return "Safari";
  if (ua.includes("firefox/")) return "Firefox";
  return "Unknown";
};

const getBrowserVersion = (userAgent: string) => {
  const ua = userAgent;
  const edge = ua.match(/Edg\/([\d.]+)/);
  if (edge) return edge[1];
  const chrome = ua.match(/Chrome\/([\d.]+)/);
  if (chrome) return chrome[1];
  const firefox = ua.match(/Firefox\/([\d.]+)/);
  if (firefox) return firefox[1];
  const safari = ua.match(/Version\/([\d.]+).*Safari/);
  if (safari) return safari[1];
  return "";
};

/* =====================
   Captured context type
===================== */
interface CapturedContext {
  path: string;
  pageDisplay: string;
  moduleName: string;
  device: string;
  browser: string;
  version: string;
  screen: string;
  role: string;
}

/* =====================
   Component
===================== */

const BugReportWidget: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [open, setOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [videos, setVideos] = useState<File[]>([]);

  const [issueType, setIssueType] = useState("");
  const [expected, setExpected] = useState("");
  const [actual, setActual] = useState("");
  const [occurrence, setOccurrence] = useState("once");
  const [severity, setSeverity] = useState("minor");
  const [success, setSuccess] = useState<boolean>(false);
  const [successId, setSuccessId] = useState<string>();

  const [contextExpanded, setContextExpanded] = useState(true);
  const [capturedContext, setCapturedContext] =
    useState<CapturedContext | null>(null);

  const { hasPermission } = usePermissions();
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.bugs);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const user = useSelector((state: RootState) => state.auth.user);
  const users = useSelector((state: RootState) => state.users.users);
  const userDetails = users.find((u) => u.id === user?.id);

  /* ── Capture context the moment the drawer opens ── */
  useEffect(() => {
    if (open) {
      const ua = navigator.userAgent || "";
      const path = window.location.pathname || "/";
      setCapturedContext({
        path,
        pageDisplay: formatPathDisplay(path),
        moduleName: getModuleNameFromPath(path),
        device: getOSInfo(ua),
        browser: getBrowserName(ua),
        version: getBrowserVersion(ua),
        screen: `${window.screen.width}x${window.screen.height}`,
        role: userDetails?.role ?? "—",
      });
    }
  }, [open, userDetails?.role]);

  const resetForm = () => {
    setIssueType("");
    setExpected("");
    setActual("");
    setOccurrence("once");
    setSeverity("minor");
    setScreenshots([]);
    setVideos([]);
    setSubmitError(null);
  };

  const handleScreenshotUpload = (files: FileList | null) => {
    if (!files) return;
    setScreenshots((prev) => [...prev, ...Array.from(files)]);
  };

  const handleVideoUpload = (files: FileList | null) => {
    if (!files) return;
    setVideos((prev) => [...prev, ...Array.from(files)]);
  };

  const removeFile = (index: number, type: "screenshot" | "video") => {
    if (type === "screenshot") {
      setScreenshots((prev) => prev.filter((_, i) => i !== index));
    } else {
      setVideos((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async () => {
    if (!hasPermission(PERMISSIONS.BUG_ADD)) return;
    try {
      setSubmitting(true);
      setSubmitError(null);
      const formData = new FormData();

      formData.append("bugtype", issueType);
      formData.append("impact", severity);
      formData.append("description", expected);
      formData.append("problem", actual);
      formData.append("occurence", occurrence);

      const ua = navigator.userAgent || "";
      const currentPath = capturedContext?.path ?? window.location.pathname;
      const moduleName =
        capturedContext?.moduleName ?? getModuleNameFromPath(currentPath);

      formData.append("module_name", moduleName);
      formData.append("page", currentPath || "unknown");
      formData.append("device", capturedContext?.device ?? getOSInfo(ua));
      formData.append(
        "browser",
        capturedContext?.browser ?? getBrowserName(ua),
      );
      formData.append(
        "version",
        capturedContext?.version ?? getBrowserVersion(ua),
      );
      formData.append(
        "screen",
        capturedContext?.screen ??
          `${window.screen.width}x${window.screen.height}`,
      );
      formData.append("reporter_role", userDetails?.role ?? "admin");

      if (screenshots.length > 0) {
        formData.append("image", screenshots[0]);
      }

      const res = await apiService.post<BugReport>(
        "/api/bug-report/bugs/",
        formData,
      );
      const id = res.data.id;

      setSuccess(true);
      setSuccessId(id);
      dispatch(fetchBugs());
      dispatch(fetchBugAdminDashboard());
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "message" in err
          ? String((err as { message?: string }).message)
          : "Failed to submit bug report.";
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (success) {
      setOpen(false);
      setSuccessOpen(true);
      resetForm();
      dispatch(resetBugState());
    }
  }, [success, dispatch]);

  return (
    <>
      {/* 🐞 Floating Icon */}
      {!open && (
        <Tooltip title="Report a bug" placement="left">
          <Fab
            color="error"
            onClick={() => setOpen(true)}
            sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300 }}
          >
            <BugReportIcon />
          </Fab>
        </Tooltip>
      )}

      {/* 👉 Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => {
          setOpen(false);
          resetForm();
        }}
        PaperProps={{
          sx: {
            width: 400,
            p: 2.5,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={2}
        >
          <Box>
            <Typography fontWeight={600} fontSize={17}>
              Report an issue you just noticed
            </Typography>
            <Typography variant="body2" color="#828282" fontSize={13} mt={0.5}>
              You are reporting a problem you just encountered while using
              Muzukuru
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={() => {
              setOpen(false);
              resetForm();
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Scrollable content */}
        <Box flex={1} overflow="auto" pr={0.5} sx={{ width: "100%" }}>
          {/* ── Auto-capture context card ── */}
          <Box
            sx={{
              border: "1px solid #E3E3E3",
              borderRadius: 2,
              mb: 2,
              overflow: "hidden",
              bgcolor: colors.primary[900],
            }}
          >
            {/* Card header */}
            <Box
              onClick={() => setContextExpanded((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                cursor: "pointer",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#22C55E",
                    flexShrink: 0,
                  }}
                />
                <Typography fontSize={13} fontWeight={500} color="#333">
                  Context captured automatically
                </Typography>
              </Box>

              {contextExpanded ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 18, color: "#828282" }} />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 18, color: "#828282" }}
                />
              )}
            </Box>

            {/* Context grid */}
            {contextExpanded && capturedContext && (
              <Box
                sx={{
                  px: 2,
                  pb: 2,
                  pt: 0.5,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  rowGap: 2,
                  columnGap: 2,
                  borderTop: "1px solid #F0F0F0",
                }}
              >
                {[
                  { label: "PAGE", value: capturedContext.pageDisplay },
                  { label: "MODULE", value: capturedContext.moduleName },
                  { label: "YOUR ROLE", value: capturedContext.role },
                  {
                    label: "BROWSER",
                    value: `${capturedContext.browser} ${capturedContext.version}`,
                  },
                  { label: "DEVICE", value: capturedContext.device },
                  { label: "SCREEN", value: capturedContext.screen },
                ].map(({ label, value }) => (
                  <Box key={label} pt={1.5}>
                    <Typography
                      fontSize={10}
                      fontWeight={600}
                      color="#9CA3AF"
                      letterSpacing={0.6}
                      mb={0.25}
                    >
                      {label}
                    </Typography>
                    <Typography fontSize={13} fontWeight={600} color="#111">
                      {value || "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Issue Type */}
          <Box mb={2} width="100%">
            <Box
              sx={{
                backgroundColor: colors.primary[900],
                borderRadius: 2,
                p: 0,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <SectionTitle>What type of issue did you encounter?</SectionTitle>

              {[
                {
                  value: "ui_issue",
                  label: "Something doesn't look right",
                  icon: <WarningAmberIcon />,
                },
                {
                  value: "feature_not_working",
                  label: "A feature isn't working",
                  icon: <HourglassEmptyIcon />,
                },
                {
                  value: "performance",
                  label: "The system feels slow",
                  icon: <SpeedIcon />,
                },
                {
                  value: "access_issue",
                  label: "I can't access something",
                  icon: <HelpOutlineIcon />,
                },
                {
                  value: "other",
                  label: "Something else",
                  icon: <DescriptionIcon />,
                },
              ].map((option) => (
                <Box
                  key={option.value}
                  onClick={() => setIssueType(option.value)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                    height: "50px",
                    py: 1,
                    borderRadius: 4,
                    cursor: "pointer",
                    mt: 1,
                    backgroundColor:
                      issueType === option.value ? "#EDE7E3" : colors.primary[900],
                    border:
                      issueType === option.value
                        ? "1px solid #5A3E2B"
                        : "1px solid #E3E3E3",
                  }}
                >
                  <Box
                    mr={1.5}
                    width={30}
                    height={30}
                    borderRadius="50%"
                    bgcolor={colors.primary[800]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box fontSize={18} color="#828282">
                      {option.icon}
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    color="#828282"
                    fontSize={14}
                    fontWeight={400}
                  >
                    {option.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* What were you trying to do */}
          <Section>
            <SectionTitle>What were you trying to do?</SectionTitle>
            <CustomTextField
              fullWidth
              multiline
              minRows={2}
              placeholder="eg I was trying to upload a booking ..."
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
              sx={{
                backgroundColor: colors.primary[800],
                borderRadius: "10px",
                "& .MuiOutlinedInput-root": {
                  border: "none",
                  height: 75,
                  alignItems: "flex-start",
                  padding: "8px 12px",
                  "& fieldset": { border: "none" },
                },
                "& .MuiInputBase-input": { padding: 0 },
              }}
            />
          </Section>

          {/* What went wrong */}
          <Section>
            <SectionTitle>What went wrong?</SectionTitle>
            <CustomTextField
              fullWidth
              multiline
              minRows={2}
              placeholder="e.g the upload button didn't respond when I clicked it"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              sx={{
                backgroundColor: colors.primary[800],
                borderRadius: "10px",
                "& .MuiOutlinedInput-root": {
                  border: "none",
                  height: 75,
                  alignItems: "flex-start",
                  padding: "8px 12px",
                  "& fieldset": { border: "none" },
                },
                "& .MuiInputBase-input": { padding: 0 },
              }}
            />
          </Section>

          {/* Occurrence */}
          <Section>
            <SectionTitle>Does it happen consistently?</SectionTitle>
            <Box display="flex" gap={1.5} flexWrap="wrap">
              {[
                { value: "once", label: "Just once" },
                { value: "sometimes", label: "Sometimes" },
                { value: "every_time", label: "Every time" },
              ].map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  clickable
                  onClick={() => setOccurrence(option.value)}
                  sx={{
                    borderRadius: "26px",
                    textTransform: "none",
                    maxWidth: "100px",
                    width: "100%",
                    height: "36px",
                    fontWeight: 500,
                    fontSize: "12px",
                    border:
                      occurrence === option.value
                        ? "1px solid #5A3E2B"
                        : "1px solid #E3E3E3",
                    backgroundColor:
                      occurrence === option.value ? "#EDE7E3" : colors.primary[800],
                    color: occurrence === option.value ? "#5A3E2B" : "#828282",
                  }}
                />
              ))}
            </Box>
          </Section>

          {/* Media Upload */}
          <Section>
            <SectionTitle>Show us what happened</SectionTitle>
            <Typography variant="body2" fontSize={10} mb={1.5}>
              A picture is worth a thousand words
            </Typography>
            <Box display="flex" gap={2} width="100%">
              <UploadTile
                label="Screenshot"
                icon={<ImageOutlined />}
                accept="image/*"
                onUpload={handleScreenshotUpload}
              />
              <UploadTile
                label="Video"
                icon={<VideocamOutlined />}
                accept="video/*"
                onUpload={handleVideoUpload}
              />
            </Box>
            {(screenshots.length > 0 || videos.length > 0) && (
              <Box mt={2} display="flex" gap={1.5} flexWrap="wrap">
                {screenshots.map((file, i) => (
                  <PreviewTile
                    key={`img-${i}`}
                    src={URL.createObjectURL(file)}
                    onRemove={() => removeFile(i, "screenshot")}
                  />
                ))}
                {videos.map((file, i) => (
                  <PreviewTile
                    key={`vid-${i}`}
                    isVideo
                    src={URL.createObjectURL(file)}
                    onRemove={() => removeFile(i, "video")}
                  />
                ))}
              </Box>
            )}
          </Section>

          {/* Severity */}
          <Section>
            <SectionTitle>How did this affect you?</SectionTitle>
            <Box display="flex" gap={1.5} flexWrap="wrap">
              {[
                { value: "minor", emoji: "😐", label: "Minor inconvenience" },
                { value: "slowed", emoji: "😕", label: "Slowed me down" },
                { value: "blocked", emoji: "😡", label: "Blocked my work" },
              ].map((option) => (
                <Chip
                  key={option.value}
                  clickable
                  onClick={() => setSeverity(option.value)}
                  label={
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      lineHeight={1.1}
                    >
                      <Typography fontSize={20} mb={0.25}>
                        {option.emoji}
                      </Typography>
                      <Typography fontSize={10} fontWeight={500}>
                        {option.label}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    height: 60,
                    maxWidth: 90,
                    width: "100%",
                    borderRadius: "10px",
                    backgroundColor:
                      severity === option.value ? "#EDE7E3" : colors.primary[800],
                    border:
                      severity === option.value
                        ? "1px solid #5A3E2B"
                        : "1px solid transparent",
                    color: "#828282",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              ))}
            </Box>
          </Section>
        </Box>

        {/* Footer */}
        <Box pt={2}>
          {submitError && (
            <Typography color="error" fontSize={12} mb={1}>
              {submitError}
            </Typography>
          )}
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={handleSubmit}
            disabled={!issueType || !actual || loading || submitting}
            sx={{ bgcolor: "#5A3E2B", "&:hover": { bgcolor: "#4A3222" } }}
          >
            {loading || submitting ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              "Send report"
            )}
          </Button>
        </Box>
      </Drawer>

      {/* Success Modal */}
      <Modal
        open={successOpen}
        onClose={() => {
          setSuccessOpen(false);
          resetForm();
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100vh"
        >
          <Box
            sx={{
              bgcolor: colors.primary[900],
              p: 3,
              borderRadius: 3,
              boxShadow: 3,
              maxWidth: 320,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: "success.light",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <Typography sx={{ color: "#fff" }} fontSize={24}>
                ✓
              </Typography>
            </Box>
            <Typography fontWeight={600} mb={0.5}>
              Issue sent to the Muzukuru tech team
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Thanks for helping improve Muzukuru.
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                gap: 1,
                bgcolor: "grey.100",
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                fontSize: 12,
                color: "text.secondary",
              }}
            >
              <span>Reference : </span>
              <strong>{successId}</strong>
              <span>• Reported</span>
            </Box>
            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
              onClick={() => {
                setSuccessOpen(false);
                resetForm();
                setSuccess(false);
                setSuccessId("");
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default BugReportWidget;

/* =========================
   Helper Components
========================= */

type UploadTileProps = {
  label: string;
  icon: ReactNode;
  accept: string;
  onUpload: (files: FileList | null) => void;
};
const UploadTile: React.FC<UploadTileProps> = ({
  label,
  icon,
  accept,
  onUpload,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
  <Box
    component="label"
    sx={{
      flex: 1,
      height: 80,
      border: "1px dashed #E3E3E3",
      width: "100%",
      borderRadius: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      bgcolor: colors.primary[900],
      "&:hover": { bgcolor: colors.primary[800] },
    }}
  >
    <input
      hidden
      type="file"
      accept={accept}
      multiple
      onChange={(e) => onUpload(e.target.files)}
    />
    <Box
      sx={{
        mb: 0.5,
        color: "#828282",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Typography fontSize={12} color="#828282">
      {label}
    </Typography>
  </Box>
  );
};

type PreviewTileProps = {
  src: string;
  isVideo?: boolean;
  onRemove: () => void;
};
const PreviewTile: React.FC<PreviewTileProps> = ({
  src,
  isVideo,
  onRemove,
}) => (
  <Box
    sx={{
      width: 100,
      height: 70,
      borderRadius: 1.5,
      overflow: "hidden",
      position: "relative",
      border: "1px solid #E0E0E0",
    }}
  >
    {isVideo ? (
      <video
        src={src}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    ) : (
      <img
        src={src}
        alt="preview"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    )}
    <IconButton
      size="small"
      onClick={onRemove}
      sx={{
        position: "absolute",
        top: 2,
        right: 2,
        bgcolor: "rgba(255,255,255,0.85)",
        "&:hover": { bgcolor: "background.paper" },
      }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

type SectionProps = { children: ReactNode };
const Section: React.FC<SectionProps> = ({ children }) => (
  <Box mt={2} mb={2} gap={1.5} width="100%" sx={{ boxSizing: "border-box" }}>
    {children}
  </Box>
);

type SectionTitleProps = { children: ReactNode };
const SectionTitle: React.FC<SectionTitleProps> = ({ children }) => (
  <Typography variant="subtitle2" mb={2} mt={1} fontWeight={500} fontSize={14}>
    {children}
  </Typography>
);
